import { LocalNotifications } from '@capacitor/local-notifications';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { Preferences } from '@capacitor/preferences';
import { isNative } from './Platform';

/**
 * Timer scheduling service.
 *
 * Problem: a meditation countdown built on `setInterval` plus `new Audio()`
 * stops dead the moment the app is backgrounded or the screen locks —
 * iOS suspends JS timers, Android Doze puts the app to sleep, and HTML5
 * audio can't play from a killed WebView.
 *
 * Solution: hand the critical timing to the OS.
 *   • Scheduled local notifications fire at exact wall-clock times even
 *     if the app is killed, in Doze, or the device is locked. The OS
 *     plays the notification sound reliably.
 *   • The in-app UI is driven by a foreground tick that recomputes
 *     `remaining = endAt - Date.now()` from a persisted timestamp, so
 *     reopening the app mid-session never loses (or doubles) time.
 *   • `KeepAwake` prevents the screen from sleeping during an active
 *     session, so the user can actually watch the countdown if they want.
 *
 * The web build (PWA / desktop) falls through these calls via the
 * `isNative()` gate — the stored `{ startedAt, endAt }` plus the
 * foreground tick is enough for a browser tab that stays open.
 */

const FINAL_ID = 9001;
const INTERVAL_ID_BASE = 9100; // 9101..9199 reserved for interval bells
const KEY_STATE = 'timer.state';

async function ensurePermission() {
    if (!isNative()) return true;
    try {
        const perm = await LocalNotifications.checkPermissions();
        if (perm.display === 'granted') return true;
        const requested = await LocalNotifications.requestPermissions();
        return requested.display === 'granted';
    } catch (err) {
        console.warn('[timer] notification permission probe failed', err);
        return false;
    }
}

/**
 * Persist timer state + schedule native notifications.
 *
 * @param {Object} opts
 * @param {number} opts.totalSeconds     Total countdown in seconds.
 * @param {number} [opts.intervalMinutes] Optional interval bell cadence.
 * @param {string} [opts.sound]          Optional bell sound filename
 *                                       bundled in the native resources
 *                                       (e.g. "bell.wav"). `null` → OS
 *                                       default notification sound.
 * @returns {{ startedAt: number, endAt: number, notificationsDenied?: boolean }}
 */
export async function startTimer({
    totalSeconds,
    intervalMinutes,
    sound
} = {}) {
    const startedAt = Date.now();
    const endAt = startedAt + totalSeconds * 1000;

    await Preferences.set({
        key: KEY_STATE,
        value: JSON.stringify({
            startedAt,
            totalSeconds,
            intervalMinutes: intervalMinutes || 0,
            endAt,
            sound: sound || null
        })
    });

    if (!isNative()) return { startedAt, endAt };

    const granted = await ensurePermission();
    if (!granted) {
        return { startedAt, endAt, notificationsDenied: true };
    }

    // Clear anything the user might have left pending from a previous
    // session — belt-and-braces against duplicate bells.
    await cancelScheduled();

    // Interval bells are scheduled via Web Audio in the modal so they play
    // silently on the Lock Screen (local notifications always show a banner).
    // This scheduler only owns the single "Meditation complete" banner — the
    // one moment where an OS-level alert is actually desirable.
    const notifications = [
        {
            id: FINAL_ID,
            title: 'Meditation complete',
            body: 'Your session has ended. Take a moment.',
            sound,
            schedule: { at: new Date(endAt), allowWhileIdle: true }
        }
    ];

    try {
        await LocalNotifications.schedule({ notifications });
    } catch (err) {
        console.warn('[timer] schedule failed', err);
    }

    try {
        await KeepAwake.keepAwake();
    } catch (err) {
        /* KeepAwake not critical — log only */
        console.warn('[timer] keepAwake failed', err);
    }

    return { startedAt, endAt };
}

/**
 * Cancel all scheduled bell notifications.
 */
async function cancelScheduled() {
    if (!isNative()) return;
    try {
        const pending = await LocalNotifications.getPending();
        const ids = pending.notifications
            .map(n => n.id)
            .filter(
                id =>
                    id === FINAL_ID ||
                    (id > INTERVAL_ID_BASE && id < INTERVAL_ID_BASE + 1000)
            );
        if (ids.length) {
            await LocalNotifications.cancel({
                notifications: ids.map(id => ({ id }))
            });
        }
    } catch (err) {
        console.warn('[timer] cancel schedule failed', err);
    }
}

/**
 * Tear down: cancel any pending bells, release KeepAwake, clear state.
 */
export async function cancelTimer() {
    await cancelScheduled();
    if (isNative()) {
        try {
            await KeepAwake.allowSleep();
        } catch (_) {
            /* non-fatal */
        }
    }
    await Preferences.remove({ key: KEY_STATE });
}

/**
 * Read the persisted timer state so the UI can rehydrate after the app
 * was backgrounded / suspended.
 */
export async function getTimerState() {
    const { value } = await Preferences.get({ key: KEY_STATE });
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch (_) {
        return null;
    }
}

/**
 * How many whole seconds remain based on the persisted `endAt`.
 * Returns 0 if the session has elapsed (or no state is stored).
 */
export function remainingSeconds(state) {
    if (!state || !state.endAt) return 0;
    const ms = state.endAt - Date.now();
    return Math.max(0, Math.round(ms / 1000));
}

export default {
    startTimer,
    cancelTimer,
    getTimerState,
    remainingSeconds
};
